import { READ_CAMPAIGN_CONTENT_PERMISSION, ROLES, type OrgType } from "../access";
import { MENU_LINKS_COPY } from "./menu-links.copy";
import type { MenuLink, MenuSection } from "./menu-links.types";

/**
 * O menu do creator — a lista, as seções e o rótulo por tipo de conta.
 *
 * Morava em `apps/web/src/app/(main)/(authenticated)/creator/links.tsx`. Subiu porque
 * o app passa a desenhar o mesmo menu: as abas e o drawer dele listam o que a
 * sidebar e o drawer do web listam, filtrados pelos mesmos claims.
 *
 * `href` é a rota do **web** e funciona como identidade do link nos três
 * frontes. O app traduz para a rota dele numa tabela própria — a regra não
 * precisa saber que `/creator/tasks` vira `/(app)/tasks` lá. `icon` é um nome,
 * não um componente: cada plataforma resolve para o seu `lucide`.
 */
/**
 * Chave dos itens do menu. Objeto `as const` em vez de `enum` (regra do
 * pacote); o tipo tem o mesmo nome do objeto para que `key: KEY_LINK` siga
 * valendo em todo consumidor. `COLLAB` vale `"agency"`, valor persistido do
 * vocabulário antigo — não mudar.
 */
export const KEY_LINK = {
  DASHBOARD: "dashboard",
  TASKS: "tasks",
  CALENDAR: "calendar",
  NOTES: "notes",
  FINANCES: "finances",
  PUBLI: "publi",
  FLOW: "flow",
  CALCULATOR: "calculator",
  PERFORMANCE: "performance",
  COLLAB: "agency",
  PROPOSALS: "proposals",
} as const;
export type KEY_LINK = (typeof KEY_LINK)[keyof typeof KEY_LINK];

/**
 * O nome do destino na barra inferior do celular: o curto, salvo quando o
 * rótulo de Collab já trocou o item (a Calculadora vira "Propostas").
 */
export function menuTabLabel(link: Pick<MenuLink, "keyTag" | "label" | "collabLabel">): string {
  if (link.collabLabel && link.label === link.collabLabel) return link.label;
  return MENU_LINKS_COPY.tabLabels[link.keyTag] ?? link.label;
}

/** Aplica o rótulo de Collab antes de exibir o menu. */
export function applyOrgTypeLabel<T extends MenuLink>(
  link: T,
  orgType: OrgType | null | undefined,
): T {
  if (orgType === "collab" && link.collabLabel) {
    return { ...link, label: link.collabLabel };
  }
  return link;
}

export const menuSections: MenuSection[] = [
  {
    get label() { return MENU_LINKS_COPY.sections.creativeCommand; },
    section: 0,
  },
  {
    get label() { return MENU_LINKS_COPY.sections.ideasStudio; },
    section: 1,
  },
  {
    get label() { return MENU_LINKS_COPY.sections.moneyOnTheTable; },
    section: 2,
  },
  {
    get label() { return MENU_LINKS_COPY.sections.yourImpact; },
    section: 3,
  },
];

const secao = (indice: number): MenuSection => {
  const encontrada = menuSections[indice];
  if (!encontrada) {
    throw new Error(`menuSections has no section ${indice}`);
  }
  return encontrada;
};

export const menuLinks: MenuLink[] = [
  {
    get label() { return MENU_LINKS_COPY.links.dashboard.label; },
    href: "/creator",
    icon: "LayoutDashboard",
    dataTestId: "creator-dashboard-link",
    section: secao(0),
    isFree: true,
    keyTag: KEY_LINK.DASHBOARD,
  },
  {
    get label() { return MENU_LINKS_COPY.links.agency.label; },
    href: "/creator/collab",
    icon: "Users",
    dataTestId: "creator-collab-link",
    section: secao(0),
    isFree: true,
    keyTag: KEY_LINK.COLLAB,
    roles: [ROLES.OWNER, ROLES.ADMIN],
    collabOnly: true,
  },
  {
    get label() { return MENU_LINKS_COPY.links.flow.label; },
    href: "/creator/workflow",
    icon: "Lightbulb",
    dataTestId: "creator-workflow-link",
    roles: [ROLES.ADMIN, ROLES.OWNER, ROLES.COMMERCIAL_PLANNER, ROLES.PLANNER],
    section: secao(0),
    keyTag: KEY_LINK.FLOW,
  },
  {
    get label() { return MENU_LINKS_COPY.links.calendar.label; },
    href: "/creator/calendar",
    icon: "CalendarRange",
    dataTestId: "creator-calendar-link",
    section: secao(0),
    isFree: true,
    keyTag: KEY_LINK.CALENDAR,
  },
  {
    get label() { return MENU_LINKS_COPY.links.notes.label; },
    href: "/creator/notes",
    icon: "NotepadText",
    dataTestId: "creator-notes-link",
    section: secao(1),
    isFree: true,
    keyTag: KEY_LINK.NOTES,
    roles: [ROLES.ADMIN, ROLES.OWNER, ROLES.PLANNER, ROLES.COMMERCIAL_PLANNER],
  },
  {
    get label() { return MENU_LINKS_COPY.links.tasks.label; },
    href: "/creator/tasks",
    keyTag: KEY_LINK.TASKS,
    icon: "NotebookPen",
    dataTestId: "creator-tasks-link",
    roles: [ROLES.ADMIN, ROLES.OWNER, ROLES.COMMERCIAL_PLANNER, ROLES.PLANNER],
    section: secao(1),
  },
  {
    get label() { return MENU_LINKS_COPY.links.publi.label; },
    href: "/creator/publi",
    icon: "Megaphone",
    dataTestId: "creator-publi-link",
    roles: [
      ROLES.ADMIN,
      ROLES.OWNER,
      ROLES.COMMERCIAL,
      ROLES.COMMERCIAL_PLANNER,
    ],
    permissions: [READ_CAMPAIGN_CONTENT_PERMISSION],
    section: secao(1),
    keyTag: KEY_LINK.PUBLI,
  },
  {
    get label() { return MENU_LINKS_COPY.links.finances.label; },
    href: "/creator/finances",
    icon: "BadgeDollarSign",
    dataTestId: "creator-finances-link",
    roles: [ROLES.ADMIN, ROLES.OWNER],
    section: secao(2),
    keyTag: KEY_LINK.FINANCES,
  },
  {
    get label() { return MENU_LINKS_COPY.links.calculator.label; },
    get collabLabel() { return MENU_LINKS_COPY.links.calculator.collabLabel; },
    href: "/creator/calculator",
    icon: "Calculator",
    dataTestId: "creator-calculator-link",
    roles: [
      ROLES.ADMIN,
      ROLES.OWNER,
      ROLES.COMMERCIAL,
      ROLES.COMMERCIAL_PLANNER,
    ],
    permissions: [READ_CAMPAIGN_CONTENT_PERMISSION],
    section: secao(2),
    keyTag: KEY_LINK.CALCULATOR,
  },
  {
    get label() { return MENU_LINKS_COPY.links.performance.label; },
    href: "/creator/performance",
    icon: "Gauge",
    dataTestId: "creator-performance-link",
    isFree: true,
    section: secao(3),
    keyTag: KEY_LINK.PERFORMANCE,
    roles: [ROLES.ADMIN, ROLES.OWNER, ROLES.PLANNER, ROLES.COMMERCIAL_PLANNER],
  },
];
